import { useMemo, useState } from 'react'
import { ClipboardCheck } from 'lucide-react'
import EmpresaSection from './FormSections/EmpresaSection'
import ContatoSection from './FormSections/ContatoSection'
import IntelligenceSection from './FormSections/IntelligenceSection'
import NecessitiesSection from './FormSections/NecessitiesSection'
import LeadPartnerField from './FormSections/LeadPartnerField'
import { CloserLeadOptionalFields } from './CloserLeadOptionalFields'
import { QrLeadScanGuide } from './QrLeadScanGuide'
import { isRocketInstance } from '@/config/instanceProfile'
import { hasCloserLeadOptionalData } from './closerLeadForm.utils'
import type { CloserLeadFormData } from '@/types'
import type { QrLeadScanResult } from '@/utils/qrLeadScanFlow'

interface CloserLeadFormProps {
  formData: CloserLeadFormData
  setFormData: (data: CloserLeadFormData | ((prev: CloserLeadFormData) => CloserLeadFormData)) => void
  mode: 'create' | 'edit'
  backendErrors?: Record<string, string>
  highlightEmpresa?: boolean
  highlightContato?: boolean
  qrFromWhatsApp?: boolean
  onEmpresaFilled?: () => void
  onContatoFilled?: () => void
  onAfterQrScan?: (result: QrLeadScanResult) => void
}

export function CloserLeadForm({
  formData,
  setFormData,
  mode,
  backendErrors = {},
  highlightEmpresa = false,
  highlightContato = false,
  qrFromWhatsApp = false,
  onEmpresaFilled,
  onContatoFilled,
  onAfterQrScan,
}: CloserLeadFormProps) {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const mergedErrors = { ...errors, ...backendErrors }
  const useSimplifiedLayout = mode === 'create'
  const optionalDefaultOpen = useMemo(
    () => mode === 'edit' && hasCloserLeadOptionalData(formData),
    [mode, formData],
  )

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => { const next = { ...prev }; delete next[field]; return next })
    }
  }

  const handleMultiFieldChange = (fields: Partial<CloserLeadFormData>) => {
    setFormData(prev => ({ ...prev, ...fields }))
    setErrors(prev => {
      const next = { ...prev }
      for (const key of Object.keys(fields)) {
        delete next[key]
      }
      return next
    })
  }

  const telefoneOk = formData.telefone.replace(/\D/g, '').length >= 8
  const nomeOk = formData.contato.trim().length >= 2
  const empresaOk = formData.empresa.trim().length >= 2
  const qrFlowActive = telefoneOk && (qrFromWhatsApp || highlightContato || highlightEmpresa)

  const empresaSection = (
    <EmpresaSection
      formData={formData}
      onFieldChange={handleFieldChange}
      onMultiFieldChange={handleMultiFieldChange}
      errors={mergedErrors}
      highlightEmpresa={highlightEmpresa}
      onEmpresaFilled={onEmpresaFilled}
    />
  )

  const contatoSection = (
    <ContatoSection
      formData={formData}
      onFieldChange={handleFieldChange}
      onMultiFieldChange={handleMultiFieldChange}
      errors={mergedErrors}
      highlightContato={highlightContato}
      onContatoFilled={onContatoFilled}
      onAfterQrScan={onAfterQrScan}
      fields={useSimplifiedLayout ? 'essential' : 'all'}
    />
  )

  const partnerSection = isRocketInstance ? (
    <LeadPartnerField formData={formData} onMultiFieldChange={handleMultiFieldChange} errors={mergedErrors} />
  ) : null

  const qrGuide = qrFlowActive ? (
    <QrLeadScanGuide
      telefoneOk={telefoneOk}
      nomeOk={nomeOk}
      empresaOk={empresaOk}
      whatsappOnlyPhone={qrFromWhatsApp}
    />
  ) : null

  const orderedSections = qrFlowActive ? (
    <>
      {contatoSection}
      {empresaSection}
    </>
  ) : (
    <>
      {empresaSection}
      {contatoSection}
    </>
  )

  if (!useSimplifiedLayout) {
    return (
      <div className='space-y-8'>
        {qrGuide}
        {orderedSections}
        {partnerSection}
        <IntelligenceSection formData={formData} onFieldChange={handleFieldChange} errors={mergedErrors} />
        <NecessitiesSection formData={formData} onFieldChange={handleFieldChange} />
      </div>
    )
  }

  return (
    <div className='space-y-5'>
      {qrGuide}

      <section className='space-y-6 rounded-2xl border-2 border-accent/20 bg-accent/[0.04] p-4 sm:p-5'>
        <header className='flex items-start gap-3'>
          <div className='rounded-xl border border-accent/20 bg-accent/10 p-2 text-accent'>
            <ClipboardCheck size={18} />
          </div>
          <div>
            <h3 className='text-sm font-bold uppercase tracking-wide text-[var(--text-primary)]'>Cadastro essencial</h3>
            <p className='mt-1 text-[12px] leading-relaxed text-[var(--text-muted)]'>
              Preencha <strong className='font-semibold text-[var(--text-primary)]'>empresa</strong>,{' '}
              <strong className='font-semibold text-[var(--text-primary)]'>nome</strong> e{' '}
              <strong className='font-semibold text-[var(--text-primary)]'>telefone</strong> para salvar.
              O <strong className='font-semibold text-[var(--text-primary)]'>site</strong> é opcional, mas fica visível aqui.
              {isRocketInstance && (
                <>
                  {' '}Informe também se o lead <strong className='font-semibold text-[var(--text-primary)]'>veio de parceiro</strong>.
                </>
              )}
            </p>
          </div>
        </header>

        {orderedSections}
        {partnerSection}
      </section>

      <CloserLeadOptionalFields
        formData={formData}
        onFieldChange={handleFieldChange}
        onMultiFieldChange={handleMultiFieldChange}
        errors={mergedErrors}
        defaultOpen={optionalDefaultOpen}
      />
    </div>
  )
}

export default CloserLeadForm
